/**
 * Centralized fetch wrapper for all AWS API Gateway calls.
 * Guarantees every request carries cache-busting headers so the browser,
 * service workers, and any CDN/proxy layers never serve stale data.
 *
 * ✅ Phase 2: Automatic idempotency key injection for mutation operations
 */

import { getApiBaseUrl, isConfigLoaded } from '../config/runtime-config';
import tokenStorage from '../services/token-storage';

// Using AWS API Gateway from runtime config (loaded at app startup)
// Returns empty string as safe fallback before config loads
export function getAPIBase(): string {
  if (!isConfigLoaded()) {
    console.warn('[api-fetch] Config not loaded yet, returning empty string');
    return ''; // Safe fallback - will be updated once config loads
  }
  return getApiBaseUrl();
}

// Export as getter function to always use latest config
// DO NOT assign at module init - config not loaded yet
export let API_BASE = '';

// This will be called from App.tsx after config loads
export function updateAPIBase() {
  API_BASE = getAPIBase();
  console.log('[api-fetch] API_BASE updated to:', API_BASE);
}

/** No custom cache headers — Cache-Control and Pragma trigger CORS preflight (OPTIONS banned) */
const NO_CACHE_HEADERS: Record<string, string> = {};

/** Standard JSON + no-cache headers (public endpoints only — no auth) */
export const baseHeaders: Record<string, string> = {
  'Content-Type': 'application/json',
  ...NO_CACHE_HEADERS,
};

/**
 * Get headers with Cognito ID token as Bearer.
 * Backend Lambda authorizers decode the idToken (not accessToken) to extract
 * user identity claims (sub, email, groups/role). Called at request time so
 * the token is always fresh after login/refresh.
 */
export function getAuthHeaders(): Record<string, string> {
  const idToken = tokenStorage.getIdToken();
  return {
    'Content-Type': 'application/json',
    ...NO_CACHE_HEADERS,
    ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
  };
}

/** Auth-only + no-cache (no Content-Type, for file uploads) */
export function getAuthHeadersNoContentType(): Record<string, string> {
  const idToken = tokenStorage.getIdToken();
  return {
    ...NO_CACHE_HEADERS,
    ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
  };
}

/** @deprecated use getAuthHeadersNoContentType() */
export const authHeaders: Record<string, string> = {
  ...NO_CACHE_HEADERS,
};

/**
 * Drop-in replacement for `fetch()` that injects no-cache directives.
 * Works with any URL, primarily intended for AWS API Gateway calls.
 *
 * - Merges caller-supplied headers with NO_CACHE_HEADERS (caller wins on conflict).
 * - Sets `cache: 'no-store'` on the RequestInit to tell the browser fetch API
 *   to bypass the HTTP cache entirely.
 * - Sets `credentials: 'omit'` — JWT Bearer token auth only (no cookies), matches axios withCredentials: false
 * - ✅ Phase 2: Automatically adds idempotency keys to mutation operations (POST, PUT, DELETE)
 */
export async function apiFetch(url: string, init?: RequestInit): Promise<Response> {
  const incomingHeaders: Record<string, string> = {};

  // Normalize incoming headers to a plain object
  if (init?.headers) {
    if (init.headers instanceof Headers) {
      init.headers.forEach((v, k) => { incomingHeaders[k] = v; });
    } else if (Array.isArray(init.headers)) {
      init.headers.forEach(([k, v]) => { incomingHeaders[k] = v; });
    } else {
      Object.assign(incomingHeaders, init.headers);
    }
  }

  const response = await fetch(url, {
    ...init,
    headers: {
      ...NO_CACHE_HEADERS,
      ...incomingHeaders,
    },
    cache: 'no-store',
    credentials: 'omit', // JWT Bearer token auth (no cookies) — matches axios withCredentials: false
  });

  // P9: 401 — token expired or invalid, redirect to login
  if (response.status === 401 && typeof window !== 'undefined') {
    // Only redirect if not already on an auth page
    if (!window.location.pathname.startsWith('/auth') && !window.location.pathname.startsWith('/login')) {
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
  }

  return response;
}
